/*!
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import Vue from 'vue'
import Router from 'vue-router'

import Login from '@/components/Login'
import MetricsSummary from '@/components/MetricsSummary'
import Homepage from '@/components/Homepage'
import LocalOpsDashboardOverview from '@/components/LocalOpsDashboardOverview'
import LocalOpsDashboardUnitEconomics from '@/components/LocalOpsDashboardUnitEconomics'
import UnitEconomicsLeaderboard from '@/components/UnitEconomicsLeaderboard'
import LocalOpsDashboardMetricsByRegion from '@/components/LocalOpsDashboardMetricsByRegion'
import DashboardList from '@/components/DashboardList'
import GoldenQuery from '@/components/GoldenQuery'
import QueryTemplateWizard from '@/components/QueryTemplateWizard'
import QueryPad from '@/components/QueryPad'
import DataCatalog from '@/components/DataCatalog'
import Forum from '@/components/Forum'
import ForumPostDetail from '@/components/ForumPostDetail'
import ForumUser from '@/components/ForumUser'
import AdminConsole from '@/components/AdminConsole'
import QueryResult from '@/components/QueryResult'
import DataCatalogStats from '@/components/DataCatalogStats'

Vue.use(Router)

const router = new Router({
  mode: 'history',
  linkActiveClass: 'ivu-menu-item-active',
  linkExactActiveClass: 'ivu-menu-item-active',
  routes: [
    {
      path: '/',
      redirect: '/home',
      name: 'Homepage',
      component: Homepage,
      props: true,
      children: [
        {
          path: '/home',
          name: 'Metrics Summary',
          props: true,
          component: MetricsSummary,
          meta: {
            module: 'Home',
            requiresAuth: true
          }
        },
        {
          path: '/local-ops-dashboard-overview',
          name: 'Local Overview',
          props: true,
          component: LocalOpsDashboardOverview,
          meta: {
            module: 'Home',
            requiresAuth: true
          }
        },
        {
          path: '/local-ops-dashboard-unit-economics',
          name: 'Unit Economics',
          props: true,
          component: LocalOpsDashboardUnitEconomics,
          meta: {
            module: 'Home',
            requiresAuth: true
          }
        },
        {
          path: '/unit-economics-leaderboard',
          name: 'Unit Economics Leaderboard',
          props: true,
          component: UnitEconomicsLeaderboard,
          meta: {
            module: 'Home',
            requiresAuth: true
          }
        },
        {
          path: '/local-ops-dashboard-metrics-by-region',
          name: 'Metrics By Region',
          props: true,
          component: LocalOpsDashboardMetricsByRegion,
          meta: {
            module: 'Home',
            requiresAuth: true
          }
        }
      ],
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/login',
      name: 'Login',
      props: true,
      component: Login,
      meta: {
        requiresAuth: false
      }
    },
    {
      path: '/dashboards',
      name: 'Dashboards',
      props: true,
      component: DashboardList,
      meta: {
        module: 'Dashboards',
        requiresAuth: true
      }
    },
    {
      path: '/dashboards/:id',
      name: 'Shared Dashboard',
      props: true,
      component: DashboardList,
      meta: {
        module: 'Dashboards',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query',
      name: 'Golden Query',
      props: true,
      component: GoldenQuery,
      meta: {
        module: 'Golden Query',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query/official',
      name: 'Golden Query Official',
      props: true,
      component: GoldenQuery,
      meta: {
        module: 'Golden Query',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query/community',
      name: 'Golden Query Community',
      props: true,
      component: GoldenQuery,
      meta: {
        module: 'Golden Query',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query/official/:id',
      name: 'Shared Official Golden Query',
      props: true,
      component: GoldenQuery,
      meta: {
        module: 'Golden Query',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query/community/:id',
      name: 'Shared Community Golden Query',
      props: true,
      component: GoldenQuery,
      meta: {
        module: 'Golden Query',
        requiresAuth: true
      }
    },
    {
      path: '/golden-query-library',
      redirect: '/golden-query'
    },
    {
      path: '/golden-query-library/:id',
      redirect: '/golden-query/:id'
    },
    {
      path: '/golden-query/:id',
      redirect: '/golden-query/official/:id'
    },
    {
      path: '/query-pad',
      name: 'Query Pad',
      props: true,
      component: QueryPad,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/query-pad/:id',
      name: 'Shared Query Pad',
      props: true,
      component: QueryPad,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/query-pad/shared-workspace/:id',
      name: 'Shared Query Pad from Shared Workspace',
      props: true,
      component: QueryPad,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/query-pad/:id/:fileName',
      name: 'Shared Query Pad With Result',
      props: true,
      component: QueryPad,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/scheduled-query/:id',
      name: 'Scheduled Query Result',
      props: true,
      component: QueryResult,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/query-template-wizard',
      name: 'Query Template Wizard',
      props: true,
      component: QueryTemplateWizard,
      meta: {
        module: 'Query Pad',
        requiresAuth: true
      }
    },
    {
      path: '/data-catalog',
      name: 'Data Catalog',
      props: true,
      component: DataCatalog,
      meta: {
        module: 'Data Catalog',
        requiresAuth: true
      }
    },
    {
      path: '/data-catalog/:databaseName',
      name: 'Shared Data Catalog (Database)',
      props: true,
      component: DataCatalog,
      meta: {
        module: 'Data Catalog',
        requiresAuth: true
      }
    },
    {
      path: '/data-catalog/:databaseName/:schemaName',
      name: 'Shared Data Catalog (Schema)',
      props: true,
      component: DataCatalog,
      meta: {
        module: 'Data Catalog',
        requiresAuth: true
      }
    },
    {
      path: '/data-catalog/:databaseName/:schemaName/:tableName',
      name: 'Shared Data Catalog (Table/View)',
      props: true,
      component: DataCatalog,
      meta: {
        module: 'Data Catalog',
        requiresAuth: true
      }
    },
    {
      path: '/forum',
      name: 'Forum',
      component: Forum,
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/forum/post/:id',
      name: 'Forum Post',
      component: ForumPostDetail,
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/forum/relatedPosts',
      name: 'Forum User',
      component: ForumUser,
      props: true,
      meta: {
        requiresAuth: true
      }
    },
    {
      path: '/admin-console',
      name: 'Admin Console',
      component: AdminConsole,
      props: true,
      meta: {
        module: 'Admin Console',
        requiresAuth: true
      }
    },
    {
      path: '/data-catalog-stats',
      name: 'Data Catalog Stats',
      component: DataCatalogStats,
      meta: {
        module: 'Data Catalog',
        requiresAuth: true
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth) && !(Vue.prototype.$auth.isAuthenticated())) {
    next({ path: '/login' })
  } else {
    next()
  }
})

export default router
